import React, { useEffect, useMemo, useState } from "react";
import "./CompanyProfileCard.css";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import {
  FiBriefcase,
  FiCalendar,
  FiEdit2,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const formatValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string" && value.trim() === "") return "-";
  return value;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const toCleanString = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return String(value).trim();
  return value.trim();
};

const editableFields = [
  "owner_name",
  "con_no",
  "email",
  "address_line_1",
  "address_line_2",
  "city",
  "state",
  "pincode",
];

const mapCompanyToForm = (company) => ({
  owner_name: toCleanString(company?.owner_name),
  con_no: toCleanString(company?.con_no),
  email: toCleanString(company?.email),
  address_line_1: toCleanString(company?.address_line_1),
  address_line_2: toCleanString(company?.address_line_2),
  city: toCleanString(company?.city),
  state: toCleanString(company?.state),
  pincode: toCleanString(company?.pincode),
});

function CompanyProfileCard({
  company,
  loading = false,
  error = "",
  onRefresh,
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [formValues, setFormValues] = useState(mapCompanyToForm(company));

  useEffect(() => {
    if (!isEditMode) {
      setFormValues(mapCompanyToForm(company));
      setSelectedLogoFile(null);
    }
  }, [company, isEditMode]);

  const rawLogo = company?.logo || company?.logo_path;
  const resolvedLogo =
    rawLogo &&
    (String(rawLogo).startsWith("http://") ||
      String(rawLogo).startsWith("https://") ||
      String(rawLogo).startsWith("data:"))
      ? rawLogo
      : rawLogo
      ? `${API_URL}${String(rawLogo).startsWith("/") ? "" : "/"}${rawLogo}`
      : "";
  const previewLogo = useMemo(() => {
    if (!selectedLogoFile) return resolvedLogo;
    return URL.createObjectURL(selectedLogoFile);
  }, [selectedLogoFile, resolvedLogo]);

  useEffect(() => {
    return () => {
      if (selectedLogoFile && previewLogo?.startsWith("blob:")) {
        URL.revokeObjectURL(previewLogo);
      }
    };
  }, [selectedLogoFile, previewLogo]);

  const firmName = formatValue(company?.firm_name);
  const ownerName = formatValue(company?.owner_name);
  const contactNo = formatValue(company?.con_no);
  const emailId = formatValue(company?.email);
  const addressLine1 = toCleanString(company?.address_line_1);
  const addressLine2 = toCleanString(company?.address_line_2);
  const city = toCleanString(company?.city);
  const state = toCleanString(company?.state);
  const pincode = toCleanString(company?.pincode);

  const cityState = [city, state].filter(Boolean).join(", ");
  const cityStatePincode = cityState && pincode
    ? `${cityState} - ${pincode}`
    : cityState || pincode || "";
  const addressLines = [addressLine1, addressLine2, cityStatePincode].filter(
    Boolean
  );

  const subscriptionEnd = formatDate(company?.subscription_end);

  const handleEdit = () => {
    setFormValues(mapCompanyToForm(company));
    setSelectedLogoFile(null);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setFormValues(mapCompanyToForm(company));
    setSelectedLogoFile(null);
    setIsEditMode(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedLogoFile(file);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const payload = new FormData();
      editableFields.forEach((field) => {
        payload.append(field, formValues[field] ?? "");
      });
      if (selectedLogoFile) {
        payload.append("logo", selectedLogoFile);
      }

      const response = await axiosInstance.put("/api/firm/profile", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbarSeverity("success");
      setSnackbarMessage(
        response?.data?.message || "Firm profile updated successfully"
      );
      setSnackbarOpen(true);

      if (onRefresh) {
        await onRefresh();
      }
      setIsEditMode(false);
      setSelectedLogoFile(null);
    } catch (saveError) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        saveError?.response?.data?.message || "Unable to update profile"
      );
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="company-profile-card">
      <div className="company-profile-head">
        <div className="company-profile-head-left">
          <div className="company-logo-box" aria-label="Company logo">
            {previewLogo && previewLogo !== "-" ? (
              <img
                src={previewLogo}
                alt="Company logo"
                className="company-logo-image"
              />
            ) : (
              <span className="company-logo-placeholder">No Logo</span>
            )}
          </div>
          <div className="company-profile-heading">
            <h3 className="company-profile-title">Company Profile</h3>
          </div>
        </div>
        <div className="company-profile-head-right">
          {!isEditMode && !loading && !error && (
            <button
              type="button"
              className="company-edit-btn"
              onClick={handleEdit}
              aria-label="Edit company profile"
              title="Edit company profile"
            >
              <FiEdit2 />
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="company-profile-state">Loading company profile...</div>
      )}

      {!loading && error && (
        <div className="company-profile-state company-profile-error">{error}</div>
      )}

      {!loading && !error && !isEditMode && (
        <div className="company-profile-details-grid">
          <div className="company-field">
            <div className="company-field-meta">
              <span className="company-field-icon" aria-hidden="true">
                <FiBriefcase />
              </span>
              <span className="company-field-label">Firm Name</span>
            </div>
            <span className="company-field-value">{firmName}</span>
          </div>

          <div className="company-field">
            <div className="company-field-meta">
              <span className="company-field-icon" aria-hidden="true">
                <FiUser />
              </span>
              <span className="company-field-label">Owner Name</span>
            </div>
            <span className="company-field-value">{ownerName}</span>
          </div>

          <div className="company-field">
            <div className="company-field-meta">
              <span className="company-field-icon" aria-hidden="true">
                <FiPhone />
              </span>
              <span className="company-field-label">Contact No</span>
            </div>
            <span className="company-field-value">{contactNo}</span>
          </div>

          <div className="company-field">
            <div className="company-field-meta">
              <span className="company-field-icon" aria-hidden="true">
                <FiMail />
              </span>
              <span className="company-field-label">Email</span>
            </div>
            <span className="company-field-value">{emailId}</span>
          </div>

          <div className="company-field company-field-full">
            <div className="company-field-meta">
              <span className="company-field-icon" aria-hidden="true">
                <FiMapPin />
              </span>
              <span className="company-field-label">Address</span>
            </div>
            {addressLines.length > 0 ? (
              <div className="company-address-block">
                {addressLines.map((line, index) => (
                  <span
                    key={`company-address-${index}`}
                    className="company-address-line"
                  >
                    {line}
                  </span>
                ))}
              </div>
            ) : (
              <span className="company-field-value company-address-value">
                Address not available
              </span>
            )}
          </div>

          <div className="company-field company-field-full company-subscription-field">
            <div className="company-field-meta">
              <span className="company-field-icon" aria-hidden="true">
                <FiCalendar />
              </span>
              <span className="company-field-label">Subscription End</span>
            </div>
            <div className="company-subscription-value-wrap">
              <span className="company-field-value company-subscription-value">
                {subscriptionEnd}
              </span>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && isEditMode && (
        <div className="company-edit-form">
          <div className="company-profile-grid company-edit-grid">
            <div className="company-field">
              <span className="company-field-label">Firm Name</span>
              <span className="company-field-value">{firmName}</span>
            </div>
            <div className="company-field">
              <span className="company-field-label">Subscription End</span>
              <span className="company-field-value">{subscriptionEnd}</span>
            </div>

            <div className="company-field">
              <label className="company-field-label" htmlFor="owner_name">
                Owner Name
              </label>
              <input
                id="owner_name"
                name="owner_name"
                className="company-edit-input"
                value={formValues.owner_name}
                onChange={handleInputChange}
              />
            </div>
            <div className="company-field">
              <label className="company-field-label" htmlFor="con_no">
                Contact No
              </label>
              <input
                id="con_no"
                name="con_no"
                className="company-edit-input"
                value={formValues.con_no}
                onChange={handleInputChange}
              />
            </div>
            <div className="company-field">
              <label className="company-field-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="company-edit-input"
                value={formValues.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="company-field">
              <label className="company-field-label" htmlFor="logo">
                Replace Logo
              </label>
              <input
                id="logo"
                name="logo"
                type="file"
                accept="image/*"
                className="company-edit-input company-file-input"
                onChange={handleLogoChange}
              />
            </div>
          </div>

          <div className="company-field company-field-full company-address-field company-edit-address">
            <span className="company-field-label">Address</span>
            <div className="company-address-edit-grid">
              <div className="company-field">
                <label className="company-field-label" htmlFor="address_line_1">
                  Address Line 1
                </label>
                <input
                  id="address_line_1"
                  name="address_line_1"
                  className="company-edit-input"
                  value={formValues.address_line_1}
                  onChange={handleInputChange}
                />
              </div>
              <div className="company-field">
                <label className="company-field-label" htmlFor="address_line_2">
                  Address Line 2
                </label>
                <input
                  id="address_line_2"
                  name="address_line_2"
                  className="company-edit-input"
                  value={formValues.address_line_2}
                  onChange={handleInputChange}
                />
              </div>
              <div className="company-field">
                <label className="company-field-label" htmlFor="city">
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  className="company-edit-input"
                  value={formValues.city}
                  onChange={handleInputChange}
                />
              </div>
              <div className="company-field">
                <label className="company-field-label" htmlFor="state">
                  State
                </label>
                <input
                  id="state"
                  name="state"
                  className="company-edit-input"
                  value={formValues.state}
                  onChange={handleInputChange}
                />
              </div>
              <div className="company-field">
                <label className="company-field-label" htmlFor="pincode">
                  Pincode
                </label>
                <input
                  id="pincode"
                  name="pincode"
                  className="company-edit-input"
                  value={formValues.pincode}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="company-edit-actions">
            <button
              type="button"
              className="company-action-btn company-action-cancel"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="company-action-btn company-action-save"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </section>
  );
}

export default CompanyProfileCard;
